import { Fragment, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { srcSetFor } from "../lib/images";
import {
  ATTIRE_SECTIONS,
  type AttireExamples,
  type AttireSection,
} from "../lib/schedule";

// Watercolor illustration of the venue — the Estate at River Run — used as the
// header band. Its paper is tinted to the site's cream so the deckled edges
// dissolve into the page (see scripts note; source is static/TheEstateAtRiverRun).
const VENUE_WATERCOLOR = "/schedule/venue-watercolor.webp";

// Google Maps link for the venue address.
const VENUE_MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=" +
  encodeURIComponent(
    "The Estate at River Run, 2421 River Road W, Maidens, Virginia 23102",
  );

export function Attire() {
  const { hash } = useLocation();

  // React Router doesn't scroll to hash targets on its own — do it here once
  // the sections are mounted (and again whenever the hash changes).
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <div className="mx-auto max-w-4xl">
      {/* Full-bleed venue band, flush under the nav (cancels the page's top
          padding). The watercolor's paper is tinted to cream, so its deckled
          edges dissolve into the page with no visible band boundary. */}
      <div className="relative left-1/2 -mt-12 w-screen -translate-x-1/2 overflow-hidden bg-cream">
        <img
          src={VENUE_WATERCOLOR}
          // The band is full-bleed but the image caps at max-w-3xl (768px), so
          // phones fetch a ~640–960w variant instead of the full 1536.
          srcSet={srcSetFor(VENUE_WATERCOLOR, [640, 960, 1280], 1536)}
          sizes="(min-width: 768px) 768px, 100vw"
          width={1536}
          height={753}
          alt="Watercolor illustration of the Estate at River Run"
          decoding="async"
          className="mx-auto block w-full max-w-3xl"
        />
      </div>

      <header className="py-8 text-center">
        <h1 className="font-serif text-5xl text-pine md:text-6xl">Schedule</h1>
        <p className="mx-auto mt-4 max-w-xl font-sans text-lg font-normal tracking-[0.02em] text-pine">
          Wedding events will take place at the{" "}
          <a
            href={VENUE_MAPS_URL}
            target="_blank"
            rel="noreferrer"
            className="underline decoration-pine/40 underline-offset-4 transition-colors hover:text-midnight-teal hover:decoration-midnight-teal"
          >
            Estate at River Run (2421 River Road W, Maidens, Virginia, 23102)
          </a>
          .
        </p>
        <p className="mx-auto mt-4 max-w-xl font-sans text-lg font-normal tracking-[0.02em] text-pine">
          All are welcome to wear Indian clothes on Friday night or Saturday
          morning if they feel comfortable! See{" "}
          <Link
            to="/faq#indian-clothes"
            className="underline decoration-pine/40 underline-offset-4 transition-colors hover:text-midnight-teal hover:decoration-midnight-teal"
          >
            Q&amp;A
          </Link>{" "}
          for more information and resources if you're looking for an outfit.
        </p>
      </header>

      {/* A single continuous center spine, punctuated by floret rungs between
          events. Content flanks it: the event to the left, dress code to the
          right. No boxes — fine lines and diamonds do all the delineating. */}
      <div className="relative mt-6">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-px -translate-x-1/2 bg-pine/15 md:block"
        />

        {ATTIRE_SECTIONS.map((section) => (
          <Fragment key={section.id}>
            <FloretRung />
            <AttireRow section={section} />
          </Fragment>
        ))}
      </div>
    </div>
  );
}

/** One event, flanking the spine: event on the left, everything attire-related
 *  (dress code, inspiration note, example outfits) in the right column. */
function AttireRow({ section }: { section: AttireSection }) {
  return (
    <section
      id={section.id}
      className="scroll-mt-24 grid gap-8 py-12 md:grid-cols-2 md:items-center md:gap-0"
    >
      {/* Event — right-aligned toward the spine. */}
      <div className="md:pr-14 md:text-right">
        {section.date && (
          <p className="font-serif text-xl font-semibold uppercase tracking-[0.15em] text-midnight-teal md:text-2xl lining-nums tabular-nums">
            <EventDate date={section.date} />
          </p>
        )}
        <h2 className="mt-1.5 font-serif text-4xl leading-[1.05] text-pine md:text-5xl">
          {section.title}
        </h2>
        {section.time && (
          <p className="mt-4 text-2xl font-semibold uppercase tracking-[0.12em] text-pine md:text-3xl lining-nums tabular-nums">
            {section.time}
          </p>
        )}
      </div>

      {/* Dress code + inspiration — everything sits in the right column. */}
      <div className="md:pl-14">
        {section.dressCodes && section.dressCodes.length > 0 && (
          <>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-midnight-teal">
              Dress Code
            </p>
            <div className="mt-2 space-y-0.5">
              {section.dressCodes.map((code, i) => (
                <p
                  key={code}
                  className={
                    i === 0
                      ? "font-serif text-2xl leading-snug text-pine md:text-3xl"
                      : "font-serif text-xl leading-snug text-pine md:text-2xl"
                  }
                >
                  {code}
                </p>
              ))}
            </div>
          </>
        )}

        {section.guidance && (
          <p className="mt-4 max-w-md font-sans text-lg font-normal tracking-[0.02em] text-pine">
            {section.guidance}
          </p>
        )}

        {section.examples && <ExampleOutfits examples={section.examples} />}
      </div>
    </section>
  );
}

/** Render a date like "Friday, April 30th, 2027" with the ordinal suffix
 *  ("th"/"st"/…) lifted into a superscript. */
function EventDate({ date }: { date: string }) {
  // split() with two capture groups yields repeating [text, number, suffix]
  // triples, so suffixes land at indices where i % 3 === 2.
  const chunks = date.split(/(\d+)(st|nd|rd|th)\b/);
  return (
    <>
      {chunks.map((chunk, i) =>
        i % 3 === 2 ? (
          <sup key={i} className="text-[0.6em]">
            {chunk}
          </sup>
        ) : (
          <Fragment key={i}>{chunk}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * Example outfit types in two deliberately unlabeled columns, introduced by
 * reassuring flavor text. Lives inside the dress-code (right) column.
 */
function ExampleOutfits({ examples }: { examples: AttireExamples }) {
  return (
    <div className="mt-6">
      <p className="max-w-md font-sans text-lg font-normal tracking-[0.02em] text-pine">
        {examples.note}
      </p>

      <div className="mt-5 grid grid-cols-2 gap-6">
        {examples.columns.map((column, i) => (
          <ul key={i} className="space-y-1.5">
            {column.map((item) => (
              <li
                key={item}
                className="flex items-center gap-2.5 font-sans text-lg font-normal tracking-[0.02em] text-pine"
              >
                <span
                  aria-hidden="true"
                  className="inline-block h-1 w-1 rotate-45 bg-sage-moss/70"
                />
                {item}
              </li>
            ))}
          </ul>
        ))}
      </div>
    </div>
  );
}

/** A fine full-width rule broken at the center by a small diamond. */
function FloretRung() {
  return (
    <div aria-hidden="true" className="flex items-center">
      <span className="h-px flex-1 bg-pine/15" />
      <span className="mx-3 inline-block h-1.5 w-1.5 rotate-45 bg-sage-moss" />
      <span className="h-px flex-1 bg-pine/15" />
    </div>
  );
}
